/**
 * Run target — what a test execution was launched against.
 *
 * The runs list groups and filters by this. Named suites, named schedules and
 * tag sets are peers: a run belongs to exactly one of them, and everything else
 * is "Ad hoc". This exists because `suite_id` alone is null for most runs — a
 * daily "All published" schedule has no suite, so the list showed `—` for
 * nearly every row even though the schedule was known all along.
 */

export type RunTargetKind = 'suite' | 'schedule' | 'tag' | 'adhoc';

export type RunTarget = {
  kind: RunTargetKind;
  /** Stable filter key. Also the chart's series key. */
  key: string;
  /** Human label, already resolved — the client renders this verbatim. */
  label: string;
};

/** The fields a run target is derived from. */
export type RunTargetSource = {
  suiteId: string | null;
  suiteName: string | null;
  /** Set for schedule-triggered runs since the run-target work landed. */
  automationRuleId: string | null;
  /** Live name of that rule, so a rename doesn't split the series. */
  automationRuleName: string | null;
  /** Carries an `automation:<name>` snapshot for schedule-triggered runs. */
  triggeredReason: string | null;
  /** Comma-joined tag set for ad-hoc tag runs (`"smoke,regression"`). */
  tagFilter: string | null;
};

export const ADHOC_RUN_TARGET_KEY = 'adhoc';

const AUTOMATION_REASON_PREFIX = 'automation:';

/**
 * A run-target key, taken apart.
 *
 * `deriveRunTarget` builds these keys and the repo's filter builder turns them
 * back into SQL. Both go through this module so the format is owned in one
 * place — hand-rolling `startsWith`/`slice` on the SQL side is exactly how the
 * two drift apart, and a key whose value legitimately contains `:` or `,`
 * (a tag set, a schedule named "Nightly: EU") makes naive splitting wrong.
 */
export type ParsedRunTargetKey =
  | { kind: 'adhoc' }
  | { kind: 'suite'; suiteId: string }
  | { kind: 'schedule'; automationRuleId: string }
  /** A schedule identified only by its frozen name — its rule was deleted. */
  | { kind: 'schedule-name'; scheduleName: string }
  | { kind: 'tag'; tagFilter: string };

/**
 * Inverse of the `key` built by {@link deriveRunTarget}. Returns null for an
 * unrecognized key so the caller can fail closed rather than ignore the filter.
 *
 * Order matters: `schedule:name:` is checked before `schedule:`, and every
 * value is taken as the whole remainder of the string, never split further.
 */
export const parseRunTargetKey = (key: string): ParsedRunTargetKey | null => {
  if (key === ADHOC_RUN_TARGET_KEY) return { kind: 'adhoc' };
  if (key.startsWith('suite:')) {
    const suiteId = key.slice('suite:'.length);
    return suiteId ? { kind: 'suite', suiteId } : null;
  }
  if (key.startsWith('schedule:name:')) {
    const scheduleName = key.slice('schedule:name:'.length);
    return scheduleName ? { kind: 'schedule-name', scheduleName } : null;
  }
  if (key.startsWith('schedule:')) {
    const automationRuleId = key.slice('schedule:'.length);
    return automationRuleId ? { kind: 'schedule', automationRuleId } : null;
  }
  if (key.startsWith('tag:')) {
    const tagFilter = key.slice('tag:'.length);
    return tagFilter ? { kind: 'tag', tagFilter } : null;
  }
  return null;
};

/** Tags are stored comma-joined; split for display and drop blanks. */
export const parseTagFilter = (tagFilter: string | null | undefined): string[] =>
  (tagFilter ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

/**
 * The schedule name snapshotted into `triggered_reason` at run time. Only a
 * fallback: it freezes at the name the rule had when the run started, so a
 * renamed schedule's older runs carry the old name. Runs that predate
 * `automation_rule_id` — and runs whose rule has since been deleted — have
 * nothing else.
 */
export const scheduleNameFromTriggeredReason = (
  triggeredReason: string | null | undefined
): string | null => {
  if (!triggeredReason?.startsWith(AUTOMATION_REASON_PREFIX)) return null;
  const name = triggeredReason.slice(AUTOMATION_REASON_PREFIX.length).trim();
  return name.length > 0 ? name : null;
};

/**
 * Precedence is suite → schedule → tag → ad hoc.
 *
 * Suite wins over schedule deliberately: a suite's health is one series whether
 * a given run was scheduled or started by hand, which is what you want when
 * reading a trend. A schedule with no suite (the "All published" case) still
 * gets its own named series.
 *
 * The fourth bucket is **not** "all published" — it also holds single-flow
 * reruns, explicit-id runs, name-pattern runs and property-scoped runs, none of
 * which persist a distinguishing column. Calling it "Ad hoc" is the honest
 * label for "everything that isn't one of the three named things".
 */
export const deriveRunTarget = (source: RunTargetSource): RunTarget => {
  if (source.suiteId) {
    return {
      kind: 'suite',
      key: `suite:${source.suiteId}`,
      label: source.suiteName ?? 'Suite',
    };
  }

  const snapshotName = scheduleNameFromTriggeredReason(source.triggeredReason);

  if (source.automationRuleId) {
    return {
      kind: 'schedule',
      key: `schedule:${source.automationRuleId}`,
      label: source.automationRuleName ?? snapshotName ?? 'Schedule',
    };
  }

  // No rule id: either the run predates the column, or its rule was deleted
  // (which cascades the automation_executions row the backfill reads). Key off
  // the frozen name — those runs can't merge with a surviving rule's series
  // anyway, so a name-shaped key loses nothing.
  if (snapshotName) {
    return {
      kind: 'schedule',
      key: `schedule:name:${snapshotName}`,
      label: snapshotName,
    };
  }

  const tags = parseTagFilter(source.tagFilter);
  if (tags.length > 0) {
    // One series per tag *set*, not per tag: a run tagged "smoke,regression"
    // was one launch, and fanning it out would make series totals stop summing
    // to the run count. Wording matches `describeScheduleTarget` so a schedule
    // and the runs it produces read the same.
    // Key off the RAW column value, not the normalized join, so the key round
    // trips to an exact SQL equality on `tag_filter`. Only the label normalizes.
    return {
      kind: 'tag',
      key: `tag:${source.tagFilter}`,
      label: `Tagged: ${tags.join(', ')}`,
    };
  }

  return { kind: 'adhoc', key: ADHOC_RUN_TARGET_KEY, label: 'Ad hoc' };
};
